import dayjs from "dayjs"

import { buildMilestoneSchedule, evaluateMilestone } from "./milestones.ts"
import type { PortalProjectPhase } from "../types/portal.types.ts"

export type TypeProgressState = "completed" | "current" | "upcoming" | "overdue" | "notStarted"

export interface TypeProgress {
  /** Share of this group's own phases that are complete — `null` only when
   * the group has zero phases (never presented as 0%, a real and different
   * fact). */
  percent: number | null
  state: TypeProgressState
  /** The phase backing the status line — the current one if any is under
   * way, else the earliest upcoming one. `null` for `completed` (nothing
   * left to point at) and `notStarted` (no schedule yet to draw one from). */
  phaseName: string | null
}

/** The compact per-project-type progress row on the portal Overview page:
 * what share of THIS type's own milestones are done, and which one is
 * driving the status line. Reuses the same completed/current/upcoming
 * classification `buildMilestoneSchedule` already applies project-wide,
 * scoped to one type's phases so a project with several types (e.g.
 * "Kitchen remodel" done, "Electrical & lighting" still in progress) never
 * blends them into one misleading whole-project number. */
export function computeTypeProgress(
  phases: PortalProjectPhase[],
  today: dayjs.Dayjs = dayjs()
): TypeProgress {
  if (phases.length === 0) return { percent: null, state: "notStarted", phaseName: null }

  const completedCount = phases.filter(
    (phase) => evaluateMilestone(phase, today).state === "completed"
  ).length
  const percent = Math.round((completedCount / phases.length) * 100)

  if (completedCount === phases.length) return { percent, state: "completed", phaseName: null }

  const schedule = buildMilestoneSchedule(phases, today)
  if (schedule.current.length > 0) {
    return { percent, state: "current", phaseName: schedule.current[0].phase.name }
  }
  if (schedule.next) return { percent, state: "upcoming", phaseName: schedule.next.phase.name }
  // Not started AND not upcoming means every remaining phase is dated in
  // the past with nobody having started it — the same fact the whole-
  // project "Schedule update needed" tag flags, scoped to this type.
  if (schedule.overdue.length > 0) {
    return { percent, state: "overdue", phaseName: schedule.overdue[0].phase.name }
  }

  return { percent, state: "notStarted", phaseName: null }
}
